/*
    This file is part of Universal Sequences.
    Universal Sequences is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequences is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Tone from 'tone';
import BufferedTS from 'buffered-ts.js';
import {TRIGGER_SAMPLE, RELEASE_SAMPLE} from 'constants/event-types.js';
import {median} from 'utils/median.js';
import {isMobile} from 'react-device-detect';
import {setSampleSlices} from 'actions/index.js';
import {getTempoAndOnsets} from 'utils/getTempo.js';
import { SOUND_DETAILS_VIEW, MIX_VIEW } from "constants/view-modes.js";
import Slideable from "instruments/Slideable.js";
import { WAVEFORM } from "constants/arrangement-modes.js";
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {A_RATE, K_RATE} from 'constants/automation-rates.js';
import ParameterContext from 'effects/ParameterContext.js';
import {AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';


const NUMBER_OF_PITCH_ENVELOPES = 8;
const TIME_16N = new Tone.Time('16n');
const TIME_32N = new Tone.Time('32n');
const DEFAULT_NOTE = Tone.Frequency("C3");

window.initSamplers=0;

var id = 0;

class Sampler extends Slideable(class{}) {
    constructor(track, sample, onNewSampler, slices, samplerData, skipGain=false) {
        super();
        this.id = id++;
        this.isChosen = true;
        window.initSamplers++;
        this.store = track.store;
        this.parameters = [];

        this.counter = 0;
        this.baseTranspose = 0;

        this.sample = sample;
        this.tempo = sample.tempo || samplerData.tempo;
        this.samplerData = samplerData;
        this.onsets = sample.onsets || [];
        this.sliceData = slices;
        this.track = track;
        this.note = null;
        this.slices = [];

        this.transposeParameter = new AbstractParameter(
            'transpose', this, (a, b) => [], {
                defaultValue: 0,
                min: -32,
                max: 32,
            });
        this.registerParameter(this.transposeParameter);

        if (!skipGain) {
            // gain node for the whole sampler, controls the volume
            this.gainNode = new Tone.Volume();
            
            this.volumeParameter = new AudioParamValueParameter(
                'volume', this.gainNode.volume , {
                    min: -100,
                    max: 0,
                    unit: 'db',
                    defaultValue: 0
                });
            this.registerParameter(this.volumeParameter);
        }

        // alpha is the timestretch factor
        this.alpha = 1;
        this.tempoSynced = false;
        this.audioBuffer = sample.audioBuffer;

        // this.setVolume(samplerData.volume);
        this.offset = samplerData.offset;
        this.transpose = samplerData.transpose;
        this.onNewSampler = onNewSampler;

        this.baseBuffer = new Tone.Buffer(sample.audioBuffer);
        // load the entire sound from the url, into baseBuffer (what we slice from) 
        slices = slices === undefined ? [{startTime: 0, endTime: this.getDuration()}] : slices;
        this.sliceData = slices;
        // create 8 pitch envelopes per sampler to be rotated through
        this.pitchEnvelopes = [];
        let numberOfPitchEnvelopes = skipGain ? 1 : NUMBER_OF_PITCH_ENVELOPES;
        for (let i=0; i < numberOfPitchEnvelopes; i++) {
            let envelope = 
            new Tone.ScaledEnvelope({
	    "attack" : 0.01,
	    "decay" : 0.01,
            "min": 0,
	    "max" : 1,
            "sustain": 1,
	        "release" : 0.01,
                "curve": "linear"
            });
            envelope._releaseCurve = 'linear';
            envelope._decayCurve = 'linear';
            this.pitchEnvelopes.push(envelope);
        }
        this.pitchEnvelopeIndex = 0;

        this.detuneSignal = new Tone.Signal(0);

        this.detuneParameter = new AudioParamValueParameter(
            'detune',
            this.detuneSignal,
            {min: -5000,
             max: 5000,
             automationRate: A_RATE,
            });
        this.registerParameter(this.detuneParameter);

        if (samplerData.detuneId) {
            this.detuneParameter.id = samplerData.detuneId;
        }

        this.setupSlide();
    }

    replaceBuffer(buffer, shouldAnalyze=true) {
        if (shouldAnalyze) {
            this.release();
        }
        this._buffer = buffer;
        this.sample.audioBuffer = buffer;
        this.baseBuffer = new Tone.Buffer(buffer);
        this.slices.slice(1)
            .map(s => s.dispose());
            
        this.slices = [this.slices[0]];

        if (shouldAnalyze) {
            this.tempo = this.analyze();
        }
        this.slice(0,undefined,undefined,undefined,undefined,true);
        if (shouldAnalyze) {
            this.sliceFirstOnset();
        }
   }

    get sequencerEngine() {
        return this.track.sequencerEngine;
    }

    registerParameter(parameter) {
        parameter.setStore(this.store);
        parameter.setContext(
            new ParameterContext()
                .forInstrument(this)
                .forTrack(this.track));
        this.parameters.push(parameter);
    }

    get transpose() {
        return this.transposeParameter.get();
    }

    set transpose(t) {
        this.transposeParameter.set(t);
    }

    init(samplerData=this.samplerData, slices=this.sliceData, skipAnalyze=false) {
        if (this.tempo === undefined && !skipAnalyze) {
            this.tempo = this.analyze();
        }
        if (this.slices.length == 0) {
            this.loadSlices(slices);
        } 

        return this;
    }

    sleep(ms) {
        return new Promise((resolve, reject) => {
            setTimeout(() => resolve(), ms);
        });
    }
    
    analyze(buffer=this.baseBuffer) {
        if (window.isNFT) {
            return;
        }
        // return;
        let dateA = new Date().getTime();
        let aubio = this.track.sequencerEngine.aubio;
        let onsetThreshold = this.state.waveform.onsetThreshold;
        let {tempo, onsets, onsetToPitch} = getTempoAndOnsets(buffer, aubio, onsetThreshold);
        this.onsets = onsets;
        this.onsetToPitch = onsetToPitch;
        let dateB = new Date().getTime();
        return tempo;
    }

    loadSlices(slices) {
        slices.map((slice) => this.newSlice(slice.startTime, slice.endTime));
    }

    getVolume() {
        return this.volumeParameter.get();
    }

    get volume() {
        return this.volumeParameter.get();
    }

    set volume(v) {
        this.volumeParameter.set(v, true, true);
    }

    // sets the volume for the sampler
    setVolume(volume) {
        if (this.volumeParameter === undefined) {
            return;
        }
        this.volumeParameter.set(volume, true, true);
    }

    getJson() {
        return {
            slices: this.slices.map((slice) => slice.getJson()),
            tempoSynced: this.tempoSynced,
            detuneId: this.detuneParameter.id,
            tempo: this.tempo,
            transpose: this.transpose,
            alpha: this.alpha,
            offset: this.offset,
            reversed: this.isReversed(),
            sampleId: this.sample.getId(),
        };
    }

    release(slice=0, transpose) {
        var sampler = this.get(slice);
        var time = Tone.Master.context.currentTime;
        let note = transpose !== undefined ? this.getNoteFromTranspose(transpose) : this.lastNote;
        sampler.triggerRelease(note, time);
        this.triggerReleaseWaveformAnimation();
    }

    getNoteFromTranspose(transpose) {
        transpose += this.track.sequencerEngine.transpose;
        transpose += this.baseTranspose;
        return getDefaultNote().transpose(transpose).transpose(this.transpose);
    }

    static getDefaultTriggerConfig(config) {
        if (config.gated === undefined) {
            config.gated = true;
        }
        if (config.sliceNumber === undefined) {
            config.sliceNumber = 0;
        }
        if (config.skipRelease === undefined) {
            config.skipRelease = false;
        }
        return config;
    }

    releaseAll(time) {
        this.slices.map((slice) => {
            slice.sampler.triggerRelease(this.lastNote, time);
        });

        if (!time) {
            this.clearSlide();
            this.releaseSlide();
            this.detuneSignal.value = this.detuneParameter.get(true);
            this.lastTranspose = 0;
        }
    }

    /**
     * triggers the sampler
     * @param config contains the following attributes:
     * gated: whether gated is on or off
     * stepData: 
     * time: when to trigger (undefined means "now")
     * transpose: the pitch to trigger the sample at
     * skipRelease: whether we should trigger a release after the attack
     */
    async trigger(config) {
        config = Sampler.getDefaultTriggerConfig(config);
        let stepData = config.stepData;
        let time = config.time;
        if (config.transpose === undefined ){
            config.transpose = config.stepData.transpose || 0;
        }

        let duration = config.noDuration ?
            null : config.stepData.duration;

        var slice = this.getSlice(config.sliceNumber);
        var sampler = slice && slice.sampler;
        if (sampler === undefined || sampler === null || !sampler.loaded) {
            return;
        }
                    
        var dur = null;
        if (duration == null) {
            this.liveNote = config.transpose + slice.transpose;
            this.release(config.sliceNumber, this.liveNote);
        } else if (typeof duration === "number") {
            dur = TIME_16N * duration;
            if (this.liveNote !== undefined) {
                this.release(config.sliceNumber, this.liveNote);
            }
            this.liveNote = undefined;
        } else {
            dur = new Tone.Time(duration);
            if (this.liveNote !== undefined) {
                this.release(config.sliceNumber, this.liveNote);
            }
            this.liveNote = undefined;
        }

        var note = this.getNoteFromTranspose(config.transpose + slice.transpose);
        note._val = Math.min(31608, note._val);

        if (window.isNFT) { // and mp3) {
            note *= 44100/Tone.Master.context.sampleRate;
        }

        var velocity = config.velocity || stepData.velocity;
        var chops = stepData.chop;
        var chopDur = dur == null ? TIME_16N : dur;
        var chopRate = chopDur / chops;

        // offset the time by offset
        time = time || Tone.Master.context.currentTime;
        /*
        if (time < Tone.Master.context.currentTime) {
            time = Tone.Master.context.currentTime + 0.001;
        }
        */
        time = (TIME_32N * this.offset) + time;
        var chopTime = time;

        // set the attack and release of this trigger
        sampler.attack = new Tone.Time(stepData.attack / 1000.0);
        sampler.release = new Tone.Time(stepData.release / 1000.0);

        let _reverse = stepData.reverse || this.sequencerEngine.reverse;
        if (_reverse !== this.isReversed(config.sliceNumber)) {
            this.reverse(_reverse, config.sliceNumber);
        }

        if (this.lastSlideTranspose !== undefined || this.hasSlide) {
            this.releaseSlide(time);
        }
        if (dur && stepData.slide) {
            this.triggerSlide(config.transpose, time, dur);
            return;
        } else if (this.lastSlideTranspose !== undefined) {
            // do a fake slide where the env goes from 0 -> 0
            // but only if we had a slide since the last real trigger
            this.triggerSlide(config.transpose, time, duration, false);
            this.hasSlide = true;
        }
        if (time) {
            let detune = this.detuneParameter.get(true);
            this.detuneSignal.setValueAtTime(detune, Math.max(0, time-0.001));
        } 

        this.lastSlideTranspose = undefined;
        this.lastSlideMax = undefined;

        let timeToTrigger = chopTime;
        // trigger the sampler, once for each chop
        for (var j=0; j < chops; j++) {
            // if this is an UNGATED trigger, then we want to release all other
            // slices before triggering this one
            if (!config.gated && this.lastNote != null) {
                this.releaseAll(chopTime);
                this.triggerReleaseWaveformAnimation(chopTime);
            }

            let pitchEnvelope = this.getPitchEnvelope(sampler, note, time, velocity, stepData); 
            if (config.gated && dur) {
                // if we know the duration and its GATED then we can
                // trigger the sample for a known duration and release it after
                // that duration
                this.triggerAttackRelease(
                    sampler,
                    note,
                    dur,
                    chopTime,
                    velocity,
                    [pitchEnvelope],
                    this.convertToStartTime(config.sliceNumber, config.start || stepData.start));
            } else {
                // otherwise just trigger the attack. When the user lifts key press
                // then it'll trigger the release later on
                this.triggerAttack(
                    sampler,
                    note,
                    chopTime,
                    velocity,
                    [pitchEnvelope],
                    this.convertToStartTime(config.sliceNumber, config.start || stepData.start));
            }

            // increase chop time (i.e. WHEN we trigger the sample) by the
            // chop rate.
            chopTime = chopTime + chopRate;
        }
        this.lastNote = note;
        this.lastTranspose = config.transpose;

        this.triggerWaveformAnimation(
            config.skipRelease ? undefined : timeToTrigger, 
            config.gated && !config.skipRelease ? dur : undefined,
            config.sliceNumber,
            config.transpose,
            this.convertToStartTime(config.sliceNumber, stepData.start));
    }

    // triggers animations in the waveform component
    // allowing users to "see" the sample being triggered
    convertToStartTime(sliceNumber, percentage=0) {
        let isReversed = false; //this.isReversed(sliceNumber);
        let off = 0;
        if (isReversed && this.track) {
            let warp = this.track.midiEffects.effects.find(x => x.name === "Warp");
            if (warp) {
                let loopLength = warp.getSliceLoopLength(0, sliceNumber);
                let stepTime = new Tone.Time('32n').toMilliseconds() ;
                loopLength *= stepTime / 1000.;
                off = Math.max(0, this.getSlice(sliceNumber).buffer.duration - loopLength);
            }
        }

        let r = (percentage / 100.0)
        if (this.track && this.track.isAudioTrack) {
            let ratio = this.track.getMaxDuration() / this.getDuration(); // this.track.getMaxDuration();
            r=(r*ratio)%1;
        }
        let x =  r * this.getSlice(sliceNumber).buffer.duration;
        return convertDuration(off + x);
    }


    getPitchEnvelope(sampler, note, time=Tone.Master.context.currentTime, velocity, stepData) {
        var midi = Tone.Frequency(note).toMidi();
	// find the closest note pitch
        let pitchEnvelope = this.pitchEnvelopes[this.pitchEnvelopeIndex];
        pitchEnvelope.cancel(Math.max(0, time - 0.001));
        
        this.pitchEnvelopeIndex = (this.pitchEnvelopeIndex + 1) % this.pitchEnvelopes.length;
        let sustain = stepData.pitchSustain === null ? 1 : stepData.pitchSustain;
	pitchEnvelope.attack = stepData.pitchAttack == null ? 0 : stepData.pitchAttack / 1000.0;
        pitchEnvelope.attack += 0.001;
	pitchEnvelope.decay = stepData.pitchDecay == null ? 0 : stepData.pitchDecay / 1000.0;
        pitchEnvelope.decay += 0.001;

        pitchEnvelope.min = 0;
	pitchEnvelope.max = (sustain) * 6 * 100;
        pitchEnvelope.sustain = sustain;
        pitchEnvelope.sustain += 0.001;

	pitchEnvelope.release = stepData.pitchRelease == null ? 0 : stepData.pitchRelease / 1000.0;
	pitchEnvelope.release += 0.001;
        return pitchEnvelope;
    }

    triggerAttack(sampler=this.get(0), note, time, velocity, pitchEnvelopes, start, output) {
        return new Promise(async (resolve, reject) => {
            const onended = (bufferSource) => {
                this.triggerReleaseWaveformAnimation();
                setTimeout(() => {
                    if (bufferSource !== undefined &&
                        bufferSource._source !== undefined &&
                        !this.sequencerEngine.context.isOffline &&
                        !this.isDisposed()) {
                        this.detuneSignal.disconnect(bufferSource._source.detune);
                        pitchEnvelopes.forEach(s => s.disconnect(bufferSource._source.detune));
                    }
                }, 4000);
                resolve();
            };
            
            // time = time || new Tone.Time(Tone.Master.context.currentTime);
            try {
                let bufferSource = sampler.triggerAttack(
                    note, time + (1/Tone.Master.context.sampleRate), velocity, start, onended, output);
                if (bufferSource !== undefined && bufferSource._source !== undefined) {
                    pitchEnvelopes.forEach(s => s.connect(bufferSource._source.detune));
                    this.detuneSignal.connect(bufferSource._source.detune);
                    pitchEnvelopes.forEach(s => s.triggerAttack(this.isSimple ? undefined : time));
                }
            } catch (e) {
                console.log(e);
            }
            this.lastNote = note;
        });
    }

    triggerAttackRelease(sampler=this.get(0), note, dur, time, velocity, pitchEnvelopes, start, output) {
        return new Promise(async (resolve, reject) => {
            const onended = pitchEnvelopes.length === 1 ?
                  () => resolve() :
                  (bufferSource) => {
                      setTimeout(() => {
                          if (bufferSource !== undefined &&
                              bufferSource._source !== undefined &&
                              !this.sequencerEngine.context.isOffline && 
                              !this.isDisposed()) {
                              this.detuneSignal.disconnect(bufferSource._source.detune);
                              pitchEnvelopes.forEach(s => s.disconnect(bufferSource._source.detune));
                          }
                      }, 4000);
                      resolve();
                  };
            
            try {
                if (time < 0) {
                    time = 0;
                }
                let bufferSource = sampler.triggerAttackRelease(
                    note, dur, time, velocity, start, onended, output);
                if (bufferSource !== undefined && bufferSource._source !== undefined) {
                    pitchEnvelopes.forEach(s => s.connect(bufferSource._source.detune));
                    this.detuneSignal.connect(bufferSource._source.detune);
                    if (pitchEnvelopes.length > 1) {
                        pitchEnvelopes.forEach(s => {
                            if (s.release === 0) {
                                s.triggerAttack(time);
                            } else {
                                s.triggerAttackRelease(dur, time);
                            }
                        });
                    } else if (pitchEnvelopes[0]) {
                        pitchEnvelopes[0].triggerAttackRelease(dur, time);
                    }
                }
            } catch (e) {
                console.log(e);
            }
            this.lastNote = note;
        });
    }
    
    getDuration() {
        let d = this.baseBuffer ? this.baseBuffer.duration : 1;
        return d;
    }

    sliceFirstOnset(onlyDoFirst=false) {
        if (this.onsets === undefined ||
            this.onsets.length === 0 ||
            this.slices.length > 1) {
            return;
        }
        let slice = this.getSlice(0);
        if (slice.startTime === 0) {
            let onsetTime = this.onsets[0] / 1000;
            if (onsetTime < slice.endTime)  {
                this.slice(0, onsetTime, slice.endTime, true);
            }
        }
        if (onlyDoFirst) {
            return;
        }
    }

    autoSlice(num) {
        let lastTime = undefined;
        let slice = this.slices[this.slices.length - 1];
        let firstOnset = 1;
        for (let i=0; i < this.onsets.length; i++) {
            if (this.onsets[i]/1000 > slice.endTime) {
                firstOnset = i;
                break;
            }
        }
        let step = Math.max(1, Math.floor((this.onsets.length) / 8));
        for (let i=0; i < Math.min(num, this.onsets.length); i++) {
            let onsetTime = this.onsets[firstOnset + i*step] / 1000;
            let nextOnset = this.onsets[firstOnset + (i+1)*step];
            if (lastTime === undefined || onsetTime - lastTime >= 0.1) {
                // next onset time
                let endTime = this.getDuration();
                if (nextOnset) {
                    endTime = nextOnset / 1000;
                }
                if (onsetTime < endTime) {
                    this.newSlice(onsetTime, endTime);
                }
                lastTime = onsetTime;
            }
        }
    }

    slice(sliceNumber, startTime=0, endTime, skipCheck=false, reverse=false, realTime=false) {
        var sampleSlice = this.getSlice(sliceNumber);
        endTime = endTime == undefined ? this.getDuration()  : endTime; 
        sampleSlice.startTime = startTime;
        sampleSlice.endTime = endTime;
        this.slices.forEach(s => s.buffer.originalCopy = undefined);
        this.slices.forEach(s => s.buffer.reversedCopy = undefined);
        var duration = (endTime - startTime) * this.alpha;
        let id = ++this.counter;
        // we dont want to unncessrily slice before the timeout is complete
        if (Tone.Master.context.isOffline || realTime) {
            if (this.baseBuffer) {
                var buffer =  this.baseBuffer.slice(startTime, endTime);
                sampleSlice.buffer.set(buffer);
                sampleSlice.refreshReverse();
                this.onNewSampler(this);
            }
        } else {
            setTimeout(() => {
                if (id === this.counter || skipCheck) {
                    if (this.baseBuffer) {
                        var buffer =  this.baseBuffer.slice(startTime, endTime);
                        sampleSlice.buffer.set(buffer);
                        sampleSlice.refreshReverse();
                        this.onNewSampler(this);
                    }
                }
            }, 50);
        }
        this.sliceData[sliceNumber] = {
            startTime: startTime,
            endTime: endTime
        };
        this.updateSlicesState();
    }

    get state() {
        return this.store.getState();
    }

    updateSlicesState() {
        this.store.dispatch(setSampleSlices(
            this.id, this.slices.map(x => x.getJson())));
    }

    syncTempo(bpm, timestretch=true) {
        if (this.tempo != null) {
            if (timestretch) {
                this.alpha = this.tempo / bpm;
                this.timestretch();
            } else {
                this.baseTranspose = ((bpm / this.tempo) - 1) * 12;
            }
        }
        this.tempoSynced = true;
    }

    unsyncTempo() {
        this.alpha = 1;
        this.tempoSynced = false;
        this.baseTranspose = 0;
    }

    async timestretch() {
        if (this.alpha == 1) {
            this.unsyncTempo();
            return;
        }

        var duration = this.getDuration();
        var olats = new BufferedTS(4096);
        let alpha = this.alpha;
        olats.alpha = alpha;
        var outputLength = duration * alpha * Tone.context.sampleRate - 4096*(this.alpha);
        var audioBuffer = Tone.context.createBuffer(2, outputLength,  Tone.context.sampleRate);
        olats.set_audio_buffer(this.baseBuffer.get());
        olats.process(audioBuffer);

        // audioBuffer is now time stretch so set timestretchBuffer to it
        // make new slices from timestretch 
    }

    reverse(reversed, slice) {
        if (slice === undefined) {
            this.baseBuffer.reverse = reversed;
            this.slices.forEach(slice => slice.sampler._buffers.isReversed = reversed);
        } else {
            let _slice = this.slices[slice % this.slices.length];
            _slice.sampler._buffers.isReversed = reversed;
        }
    }

    isReversed(slice) {
        if (slice === undefined) {
            return this.baseBuffer === undefined ? false : this.baseBuffer.reverse;
        } else {
            return this.slices[slice % this.slices.length].sampler._buffers.isReversed;
        }
    } 

    deleteSlice(sliceNumber) {
        if (this.slices.length === 1) {
            // can't delete the last slice
            return;
        }
        this.slices.splice(sliceNumber, 1);
        this.sliceData.splice(sliceNumber, 1);
        this.updateSlicesState();
    }

    newSlice(
        startTime=this.slices[0] && this.slices[0].startTime,
        endTime=this.slices[0] && this.slices[0].endTime)
    {
        if (startTime === undefined && endTime === undefined) {
            return;
        }
        endTime = endTime || this.getDuration();
        var buffer;
        if (window.isNFT) {
            buffer = endTime === this.getDuration() && startTime === 0 ?
                this.baseBuffer : this.baseBuffer.slice(startTime, endTime);
        } else {
            buffer = this.baseBuffer.slice(startTime, endTime);
        }

        let sampler = new Tone.Sampler({"C3": buffer},{curve:'linear'});
        var slice = new Slice(buffer, sampler , startTime, endTime);
        slice.sampler.connect(this.gainNode);
        this.slices.push(slice);
        this.sliceData.push({startTime: startTime, endTime: endTime});
        this.slice(this.slices.length - 1, startTime, endTime, true);
        return this.slices[this.slices.length - 1];
    }

    getStartTime(i) {
        return this.slices[i].startTime;
    }

    getEndTime(i) {
        if (this.slices[i].endTime == null)  {
            this.slices[i].endTime = this.slices[0].buffer._buffer.duration;
        }
        return this.slices[i].endTime;
    }

    getNumberOfSlices() {
        return this.slices.length;
    }

    dispose() {
        this.audioBuffer = undefined;
        if (!this.isCopied) {
            this.baseBuffer.dispose();
            // this.baseBuffer = undefined;
            this.slices.forEach(s => s.dispose());
        }
        this.gainNode.dispose();
        this.pitchEnvelopes.forEach(
            p => p.dispose());

        this.baseBuffer = undefined;
        this.slices = undefined;
        this.gainNode = undefined;
        this.pitchEnvelopes = undefined;
    }

    get(sliceNumber) {
        return this.slices[sliceNumber % this.slices.length].sampler;
    }

    getSlice(sliceNumber) {
        return this.slices[sliceNumber % this.slices.length];
    }

    isLoaded() {
        return this.slices.length > 0 && this.slices[0].sampler.loaded;
    }

    triggerWaveformAnimation(time, dur, sliceNumber, transpose, sliceStartTime) {
        if (!this.showWaveformAnimation()) {
            // we only trigger animations when the waveform
            // for this track is shown, OR the mix view is shown
            return;
        }
        let slice = this.getSlice(sliceNumber);
        let startTime = slice.startTime + sliceStartTime;
        let event = new Event(TRIGGER_SAMPLE);
        event.trackNumber = this.track.trackNumber;
        event.subSequenceId = this.track.subSequence.id;
        event.startTime = startTime;
        event.duration = dur || 0;
        event.soundId = this.sample.getId();
        event.sliceStartTime = sliceStartTime;
        event.sliceDuration = slice.getDuration();
        event.sliceNumber = sliceNumber;
        event.transpose = transpose;
        event.time = Math.max(
            0,
            time === undefined ? 0 :
                this.track.sequencerEngine.getMilliseconds(time));
        window.dispatchEvent(event);
    }

    triggerReleaseWaveformAnimation(time) {
        if (this.showWaveformAnimation()) {
            let event = new Event(RELEASE_SAMPLE);
            event.trackNumber = this.track.trackNumber;
            event.subSequenceId = this.track.subSequence.id;
            event.soundId = this.sample.getId();
            event.time = Math.max(
                0,
                time === undefined ?
                    0 : this.track.sequencerEngine.getMilliseconds(time));
            window.dispatchEvent(event);
        }
    }

    showWaveformAnimation() {
        return this.track.sequencerEngine.isSampler || 
            this.isMixViewShown() ||
            this.isWaveformShown();
    }

    isMixViewShown() {
        let trackSubSequence = this.track.subSequence.id;
        let currentSubSequence = this.track.sequencerEngine.currentSubSequence.id;

        if (currentSubSequence === trackSubSequence &&
            this.state.sequencer.viewMode === MIX_VIEW) {
            return true;
        }

        return false;
    }

    isWaveformShown() {
        if (this.showingPrettyWaveform) {
            return true;
        }
        let trackSubSequence = this.track.subSequence.id;
        let currentSubSequence = this.track.sequencerEngine.currentSubSequence.id;
        return trackSubSequence === currentSubSequence &&
            this.state.sequencer.waveformShowing && 
            this.track.sequencerEngine.cursor.track === this.track.trackNumber;
    }

    getCopy(onNewSampler, track=this.track) {
        let samplerData = {
            volume: this.volume,
            offset: this.offset,
            transpose: this.transpose,
            reversed: this.isReversed(),
        };

        let sampler = new Sampler(track, this.sample, onNewSampler, this.sliceData, samplerData);
        sampler.tempo = this.tempo;
        sampler.onsets = this.onsets;
        sampler.init();
        this.isCopied = true;

        sampler.slices.forEach((s, i) => {
            if (this.slices[i]) {
                s.buffer.originalCopy = this.slices[i].buffer.originalCopy;
                s.buffer.reversedCopy = this.slices[i].buffer.reversedCopy;
            }
        });
        return sampler;
    }

    isDisposed() {
        return this.audioBuffer === undefined;
    }
}

class Slice {
    constructor(buffer, sampler, startTime, endTime, transpose=0) {
        this.buffer = buffer;
        this.transpose = transpose;
        this.sampler = sampler;
        this.startTime = startTime == undefined ? 0 : startTime;
        this.endTime = endTime;
        this.slice = undefined;
    }

    getDuration() {
        return this.endTime - this.startTime;
    }

    dispose() {
        this.buffer.dispose();
        this.sampler.dispose();
        this.buffer = undefined;
        this.sampler = undefined;
        this.startTime = undefined;
        this.endTime = undefined;
    }

    getJson() {
        return {
            startTime: this.startTime,
            endTime: this.endTime,
            transpose: this.transpose,
        };
    }

    getCopy() {
        return new Slice(this.buffer, this.sampler, this.startTime, this.endTime, this.transpose);
    }

    refreshReverse() {
        let buffers = this.sampler._buffers;
        buffers.isReversed = false;
        buffers._reversedBuffers["48"] =
            buffers.getReversed(buffers._buffers["48"]);
    }

}

export default Sampler;

function getDefaultNote() {
    return DEFAULT_NOTE;
}

const convertDuration = (d) => {
    if (!window.isNFT) {
        return d;
    }
    return d * (44100/Tone.Master.context.sampleRate);
}
